/**
 * Turns a measured avatar-frame ring (`avatar-frame-metrics.generated.ts`)
 * into the box, offsets and scale `AvatarDisplay` renders with.
 *
 * The point of the exercise: an avatar frame is a ring plus decoration hung
 * off it, and only the ring frames the avatar. Fitting the whole PNG into a
 * fixed box and centring the avatar in it — which is what the profile hero
 * used to do — works only while every frame's decoration is radially
 * symmetric. It is not: a laurel sweeping across the bottom drags the image
 * centre below the ring centre, and a heavier wreath takes a smaller share of
 * the image, so the avatar comes out both offset and the wrong size. Adding
 * one frame then quietly re-breaks the framing of the ones already shipped.
 *
 * So nothing here is derived from the image bounds. The art is scaled until
 * its measured ring opening matches the avatar, then offset so the ring's
 * centre lands on the avatar's, and the reserved box is grown symmetrically
 * around that point until the art fits inside it. Whatever a new frame hangs
 * off its ring only changes how much empty space the box reserves.
 */
import { AVATAR_FRAME_METRICS, type AvatarFrameMetrics } from './avatar-frame-metrics.generated.js';

/**
 * Geometry of `profile/placeholders/profile-avatar-frame.svg`, the frame drawn
 * for an empty or unrecognised selection. Read off that file's viewBox (a
 * 220-unit square whose inner dashed circle has radius 87, drawn with a
 * 2.5-wide stroke straddling it, so the opening's edge is at 85.75) rather
 * than measured: it is a structural placeholder, not art from the intake
 * pipeline, so no master exists for `pnpm assets:frames` to read.
 */
export const PLACEHOLDER_FRAME_METRICS: AvatarFrameMetrics = {
  aspectRatio: 1,
  ringCenterX: 0.5,
  ringCenterY: 0.5,
  ringRadius: 85.75 / 220,
};

/**
 * How much wider the avatar is than the ring's opening. A hair of overlap, so
 * the avatar's edge tucks under the ring instead of leaving a sliver of hero
 * background showing between the two on a fractional-pixel boundary.
 */
export const RING_OVERLAP = 1.03;

export interface AvatarFrameLayout {
  /** Side of the square box the component lays out, in px. */
  readonly box: number;
  /** Rendered frame art size, in px. */
  readonly frameWidth: number;
  readonly frameHeight: number;
  /** Where the frame art is placed inside the box, in px from its top-left. */
  readonly frameLeft: number;
  readonly frameTop: number;
  /** Where the avatar is placed inside the box, in px from its top-left. */
  readonly avatarLeft: number;
  readonly avatarTop: number;
}

export function avatarFrameMetrics(frameId: string | null): AvatarFrameMetrics {
  return (frameId ? AVATAR_FRAME_METRICS[frameId] : undefined) ?? PLACEHOLDER_FRAME_METRICS;
}

/**
 * Lays out a frame around an avatar of `size` px.
 *
 * The box is square and centred on the ring, not on the artwork, so however
 * lopsided a frame's decoration is the avatar still sits at the centre of the
 * space this reserves — which is what the hero's grid centres and what the
 * identity block aligns against.
 */
export function avatarFrameLayout(metrics: AvatarFrameMetrics, size: number): AvatarFrameLayout {
  const frameWidth = size / (2 * metrics.ringRadius * RING_OVERLAP);
  const frameHeight = frameWidth / metrics.aspectRatio;
  const ringX = metrics.ringCenterX * frameWidth;
  const ringY = metrics.ringCenterY * frameHeight;

  const box = Math.ceil(2 * Math.max(ringX, frameWidth - ringX, ringY, frameHeight - ringY));
  const center = box / 2;

  return {
    box,
    frameWidth,
    frameHeight,
    frameLeft: center - ringX,
    frameTop: center - ringY,
    avatarLeft: center - size / 2,
    avatarTop: center - size / 2,
  };
}
